/**
 * "Rapportera fel / Report an issue" — the small link every content item carries
 * (spec §4.1 content). Posts to the foundation route `POST /api/content/reports`.
 */
import { useMutation } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { useState } from "react";
import { api } from "@client/api/client";
import { useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { Button, Modal, Textarea, useToast } from "./ui";

export interface ReportIssueProps {
  questionId: string;
  className?: string;
}

export function ReportIssue({ questionId, className }: ReportIssueProps) {
  const t = useT();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  const mutation = useMutation({
    mutationFn: (body: { questionId: string; note: string }) =>
      api.post<{ report: { id: string } }>("/api/content/reports", body),
    onSuccess: () => {
      toast.success(t("report.sent"));
      setOpen(false);
      setNote("");
    },
    onError: (error) => toast.error(messageForError(error, t)),
  });

  const send = () => {
    if (note.trim().length < 3) return;
    mutation.mutate({ questionId, note: note.trim() });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 max-sm:min-h-11 text-xs text-ink-500 underline underline-offset-2 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100 no-print ${className ?? ""}`}
      >
        <Flag size={12} aria-hidden="true" />
        {t("report.link")}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t("report.title")}
        description={questionId}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={send} loading={mutation.isPending} disabled={note.trim().length < 3}>
              {t("report.send")}
            </Button>
          </>
        }
      >
        <Textarea
          label={t("report.note")}
          placeholder={t("report.placeholder")}
          value={note}
          rows={5}
          onChange={(event) => setNote(event.target.value)}
          onSubmitShortcut={send}
        />
      </Modal>
    </>
  );
}
